import b from 'b_';
import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import SnackStore from '../../../stores/SnackStore';
import PolicyStore from '../../../stores/Policy/PolicyStore';
import FuseMenu from '../../FuseControls/FuseMenu';
import FuseAlert from '../../FuseControls/FuseAlert';
import Page from '../../Fuse/Page';
import { FTBody } from '../../Fuse/Text';
import PolicyTable from '../PolicyTable';
import PriorityModal from '../PriorityModal';
import AccessCodesModal from './AccessCodesModal';
import copyText from '../../../copyText';
import TextWithTitle from '../../ConsoleFragments/TextWithTitle';
import { ModalMode } from '../../../stores/Policy/PolicyModalStore';
import { getStatus, getGmtDateString } from '../../../stores/Policy/AccessCodes/AccessCodeInfoStore';
import Card from '../../Fuse/Card';
import ConsoleStore from '../../../stores/ConsoleStore';
import { ROLE_READ_ONLY } from '../../../stores/Administrators/AdministratorModalStore';
import './AccessCodes.css';

const block = b.lock('AccessCodes');

class AccessCodes extends Component {
    static propTypes = {
        console: ConsoleStore.PropType,
        snack: SnackStore.PropType.isRequired,
        policy: PolicyStore.PropType.isRequired,
    };

    onCopyCodeToClipboard = (code) => {
        if (copyText(code)) {
            this.props.snack.showSuccess(<span>Access code <b>{code}</b> has been copied to your clipboard.</span>);
        } else {
            this.props.snack.showError('Unable to copy');
        }
    };

    columnsConfig = {
        colFilter: ({ index }) => ({
            [PolicyTable.COL_INDEXES.MENU]: false
        })[index],
        extraColumns: [{
            name: 'code',
            title: 'Code',
            render: ({ code }) => <TextWithTitle text={code} />,
            index: PolicyTable.COL_INDEXES.STATUS - 1,
        }, {
            name: 'status',
            title: 'Status',
            render: (item) => {
                const status = getStatus(item);
                return <span className={block('Warning', { isRed: status.isRed })} title={status.name}>
                    {status.name}
                </span>;
            },
            index: PolicyTable.COL_INDEXES.STATUS + 1,
        }, {
            name: 'browsingRole',
            title: 'Browsing Role',
            render: ({ browsingRoleName }) => <TextWithTitle text={browsingRoleName} />,
            index: PolicyTable.COL_INDEXES.STATUS + 2,
        }, {
            name: 'expirationDate',
            title: 'Expiration Date',
            render: ({ expirationDate, neverExpire }) => <TextWithTitle
                text={neverExpire ? 'Never Expire' : getGmtDateString(expirationDate)}
            />,
            index: PolicyTable.COL_INDEXES.STATUS + 3,
        }, {
            name: 'activationLimit',
            title: 'Activation Limit',
            render: (item) => <TextWithTitle
                text={item.unlimitedActivations ? 'Unlimited' : item.activationsAllowed}
            />,
            index: PolicyTable.COL_INDEXES.STATUS + 4,
        }, {
            name: 'totalActivations',
            title: 'Activations',
            render: ({ activations }) => activations,
            index: PolicyTable.COL_INDEXES.STATUS + 5,
        }, {
            name: 'codeMenu',
            title: '',
            render: (item) => {
                const store = this.props.policy.accessCodes.policyTable;
                return <FuseMenu
                    open={store.menu.id === item.id}
                    toggle={() => store.menu.toggle(item.id)}
                    items={[
                        {
                            label: 'Edit Code…',
                            icon: 'edit',
                            onClick: () => store.openModal(ModalMode.EDIT, item),
                        },
                        {
                            label: 'Copy Code',
                            icon: 'copy',
                            onClick: () => this.onCopyCodeToClipboard(item.code),
                        },
                        {
                            label: 'Delete Code…',
                            icon: 'delete',
                            disabled: !!item.isDefault,
                            onClick: () => store.openModal(ModalMode.DELETE, item),
                        },
                    ]}
                />;
            },
            index: PolicyTable.COL_INDEXES.STATUS + 6,
        }]
    };

    searchFilter = (term) => (arr) =>
        (term === '' ? arr : arr.filter((entity) => {
            const check = (entry) => entry.toLowerCase().indexOf(term.toLowerCase()) > -1;
            return check(entity.code) || check(entity.browsingRoleName);
        }))

    renderTable = (priorityMode) => {
        const readOnly = this.props.console.authority === ROLE_READ_ONLY.value;
        this.columnsConfig.extraColumns = this.columnsConfig.extraColumns
            .filter((column) => column.name !== 'codeMenu' || !readOnly);

        return <PolicyTable
            columnsConfig={this.columnsConfig}
            store={this.props.policy.accessCodes.policyTable}
            snack={this.props.snack}
            readOnly={readOnly}
            priorityMode={priorityMode}
            cellClassName={block('AccessCodeCell')}
            searchPlaceholder="Search by Code or Browsing Role"
            noItemsText="adding an access code"
            itemName="Code"
            searchFilter={this.searchFilter}
            loadFailedMessage="Failed to load codes."
            sortFn={(x, y) => y.id - x.id}
        />;
    }

    renderAboutSection = () => {
        return <Card title="Overview: Access Codes">
            <FuseAlert>
                Access Codes is a Legacy feature that will be removed in the future.
                The recommended method of providing a user with unique Isolation Options is to add their
                email to a Browsing Role.
            </FuseAlert>
            <FTBody>
                <p><b>Access Codes</b> allow users to assign themselves a specific Browsing Role.</p>
                <p>
                    When a Browser/Email Isolation user requests a URL, the system is designed to assign their
                    Browsing Role based on their email address. Access Codes allow users to bypass this process.
                </p>
                <p>
                    When a valid Access Code is entered into an Isolation browser, the system is prevented from
                    assigning a Browsing Role based on the user’s email. For all subsequent URL requests,
                    only the Access Code’s Browsing Role will be assigned.
                </p>
                <p>
                    The characters that comprise each Access Code are random, and system generated. Only users who
                    enter a valid Access Code into their Isolation browser can change their Browsing Role.
                </p>
                <p>
                    To add an Access Code, you must define its Browsing Role, Activation Limit, and Expiration Status.
                    Administrators are responsible for securely distributing the code to its intended users.
                </p>
            </FTBody>
        </Card>;
    }

    render() {
        const { policyTable } = this.props.policy.exclusionRules;

        return <Fragment>
            <Page title={<Fragment>Policies - Access Codes<span
                className={block('TitleTag')}
            >Legacy</span></Fragment>}
            >
                {this.renderTable(false)}
                {this.renderAboutSection()}
            </Page>

            <AccessCodesModal />

            <PriorityModal
                store={policyTable}
                subTitle={' - Access Codes'}
                errMsg={'Failed to change access code priorities'}
            >
                {this.renderTable(true)}
            </PriorityModal>
        </Fragment>;
    }
}

export default inject('console', 'snack', 'policy')(observer(AccessCodes));
